import "@/App.css";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import Header from "@/components/Header";
import ScrollToTop from "@/components/ScrollToTop";
import PageTransition from "@/components/PageTransition";
import HomePage from "@/pages/HomePage";
import AdminLogin from "@/admin/pages/AdminLogin";
import AdminLayout from "@/admin/components/AdminLayout";
import ProtectedRoute from "@/admin/components/ProtectedRoute";
import AdminDashboard from "@/admin/pages/AdminDashboard";
import AdminProjectList from "@/admin/pages/AdminProjectList";
import AdminProjectEdit from "@/admin/pages/AdminProjectEdit";
import AdminMessages from "@/admin/pages/AdminMessages";
import AdminDreams from "@/admin/pages/AdminDreams";
import AdminCompany from "@/admin/pages/AdminCompany";
import AdminServices from "@/admin/pages/AdminServices";
import {
  loadAboutPage,
  loadServicesPage,
  loadProjectsPage,
  loadProjectDetailPage,
  loadContactPage,
  loadDreamsPage,
  warmRouteChunks,
  preloadRouteImages,
  warmHeroImages,
} from "@/lib/pageLoaders";

const AboutPage = lazy(loadAboutPage);
const ServicesPage = lazy(loadServicesPage);
const ProjectsPage = lazy(loadProjectsPage);
const ProjectDetailPage = lazy(loadProjectDetailPage);
const ContactPage = lazy(loadContactPage);
const DreamsPage = lazy(loadDreamsPage);
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));

const PageFallback = () => null;

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <PageTransition>
              <HomePage />
            </PageTransition>
          }
        />
        <Route
          path="/hakkimizda"
          element={
            <PageTransition>
              <Suspense fallback={<PageFallback />}>
                <AboutPage />
              </Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/hizmetler"
          element={
            <PageTransition>
              <Suspense fallback={<PageFallback />}>
                <ServicesPage />
              </Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/projeler"
          element={
            <PageTransition>
              <Suspense fallback={<PageFallback />}>
                <ProjectsPage />
              </Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/projeler/:projectId"
          element={
            <PageTransition>
              <Suspense fallback={<PageFallback />}>
                <ProjectDetailPage />
              </Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/iletisim"
          element={
            <PageTransition>
              <Suspense fallback={<PageFallback />}>
                <ContactPage />
              </Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/dreams"
          element={
            <PageTransition>
              <Suspense fallback={<PageFallback />}>
                <DreamsPage />
              </Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/gizlilik-politikasi"
          element={
            <PageTransition>
              <Suspense fallback={<PageFallback />}>
                <PrivacyPage />
              </Suspense>
            </PageTransition>
          }
        />
        <Route
          path="/kullanim-sartlari"
          element={
            <PageTransition>
              <Suspense fallback={<PageFallback />}>
                <TermsPage />
              </Suspense>
            </PageTransition>
          }
        />
      </Routes>
    </AnimatePresence>
  );
};

const AppContent = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  return (
    <>
      <ScrollToTop />
      {isAdmin ? (
        <Routes>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/giris" element={<Navigate to="/admin/login" replace />} />
          <Route element={<ProtectedRoute />}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="projeler" element={<AdminProjectList />} />
              <Route path="projeler/:id/duzenle" element={<AdminProjectEdit />} />
              <Route path="hizmetler" element={<AdminServices />} />
              <Route path="mesajlar" element={<AdminMessages />} />
              <Route path="hayalim-talepleri" element={<AdminDreams />} />
              <Route path="sirket" element={<AdminCompany />} />
            </Route>
          </Route>
        </Routes>
      ) : (
        <>
          <Header />
          <AnimatedRoutes />
        </>
      )}
    </>
  );
};

function App() {
  useEffect(() => {
    preloadRouteImages();

    // Diğer hero görsellerini 300ms sonra ısıt — ilk hero'nun önceliğini koru
    const warmImagesId = window.setTimeout(warmHeroImages, 300);

    let idleId;
    let timeoutId;
    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(() => warmRouteChunks(), { timeout: 3000 });
    } else {
      timeoutId = window.setTimeout(warmRouteChunks, 2500);
    }

    return () => {
      window.clearTimeout(warmImagesId);
      if (idleId && "cancelIdleCallback" in window) window.cancelIdleCallback(idleId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </div>
  );
}

export default App;
